import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, WHATSAPP_URL, WHATSAPP_URL_EN, ASSETS } from "../../lib/content";
import { scrollToId, scrollToTop } from "../../lib/scroll";
import { useLang } from "../../lib/i18n";
import { SocialLinks } from "./SocialLinks";

const LangToggle = ({ idPrefix = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid={`${idPrefix}lang-toggle`}
      className="flex items-center gap-0.5 font-sans-ui text-[11px] uppercase tracking-[0.12em] border border-white/20 rounded-full px-1.5 py-1"
    >
      <button
        data-testid={`${idPrefix}lang-it`}
        onClick={() => setLang("it")}
        className={`px-2 py-0.5 rounded-full transition-colors duration-300 ${
          lang === "it"
            ? "bg-gold text-[#050505] font-semibold"
            : "text-white/60 hover:text-white"
        }`}
      >
        IT
      </button>
      <button
        data-testid={`${idPrefix}lang-en`}
        onClick={() => setLang("en")}
        className={`px-2 py-0.5 rounded-full transition-colors duration-300 ${
          lang === "en"
            ? "bg-gold text-[#050505] font-semibold"
            : "text-white/60 hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, tr } = useLang();
  const navLabel = (l) => (lang === "it" ? l.label : l.en);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4 xl:gap-8">
        {/* Mobile brand (left) */}
        <button
          data-testid="brand-logo-mobile"
          onClick={() => scrollToTop()}
          className="lg:hidden flex items-center"
        >
          <img
            src={ASSETS.logo}
            alt="Atelier Divine"
            className={`w-auto aspect-square object-cover rounded-full shadow-lg shadow-black/40 transition-all duration-500 ${
              scrolled ? "h-10" : "h-11"
            }`}
          />
        </button>

        {/* Desktop nav with inline logo between Outlet and Nel Mondo */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
          {NAV_LINKS.slice(0, 3).map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-sans-ui text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-gold transition-colors duration-300 whitespace-nowrap"
            >
              {navLabel(l)}
            </button>
          ))}

          <button
            data-testid="brand-logo"
            onClick={() => scrollToTop()}
            className="flex items-center mx-1 shrink-0"
          >
            <img
              src={ASSETS.logo}
              alt="Atelier Divine"
              className={`w-auto aspect-square object-cover rounded-full shadow-lg shadow-black/40 transition-all duration-500 ${
                scrolled ? "h-11" : "h-12"
              }`}
            />
          </button>

          {NAV_LINKS.slice(3).map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-sans-ui text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-gold transition-colors duration-300 whitespace-nowrap"
            >
              {navLabel(l)}
            </button>
          ))}
        </nav>

        {/* Right: WhatsApp + lang (mobile) + burger */}
        <div className="flex items-center justify-end gap-3 lg:gap-4">
          <a
            data-testid="header-whatsapp-cta"
            href={tr(WHATSAPP_URL, WHATSAPP_URL_EN)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-sans-ui hover:bg-gold hover:text-[#050505] transition-colors duration-400 whitespace-nowrap"
          >
            <Phone size={13} /> {tr("Prenota", "Book Now")}
          </a>
          <div className="lg:hidden">
            <LangToggle />
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Desktop second row: socials + language */}
      <div
        data-testid="header-secondary-row"
        className="hidden lg:block border-t border-white/[0.06]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-11 flex items-center justify-end gap-6">
          <SocialLinks size={15} gap="gap-4" />
          <span className="h-4 w-px bg-white/15" aria-hidden="true" />
          <LangToggle idPrefix="desktop-" />
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="font-serif-display text-2xl text-white/85 text-left"
                >
                  {navLabel(l)}
                </button>
              ))}
              <a
                href={tr(WHATSAPP_URL, WHATSAPP_URL_EN)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 border border-gold/50 text-gold px-5 py-3 text-xs uppercase tracking-[0.2em] justify-center"
              >
                <Phone size={14} /> {tr("Prenota Appuntamento", "Book an Appointment")}
              </a>
              <SocialLinks size={20} className="mt-4 justify-center" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
